import { z } from 'zod';

import {
  DESCRIBE_WORD_TITLE,
  describeCategorySchema,
  describePlayerViewSchema,
  describeRemoteSchema,
  describeTeacherViewSchema,
} from './describe-word.js';
import { FIND_FOUR_TITLE } from './find-four.js';
import { teamShapeSchema } from './palette.js';

/**
 * The shapes that travel over the wire in the lobby.
 *
 * Two audiences with deliberately different views. The teacher sees the whole
 * room; a player sees themselves and their own team and nothing else. That
 * asymmetry is enforced by the namespace split (§9.1) and by these types --
 * there is no roster field on the player's view to accidentally populate.
 */

export const sessionStatusSchema = z.enum([
  'LOBBY',
  'INTERMISSION',
  'IN_PROGRESS',
  'PAUSED',
  'FINISHED',
  'ABANDONED',
]);

export type SessionStatus = z.infer<typeof sessionStatusSchema>;

/** A game's board may carry at most this many distinct exercises. */
export const MAX_GAME_EXERCISES = 8;

/**
 * How a game assigns exercises to a round.
 *
 * `per_tile` is the original board: each interior square is one exercise, and
 * a team plays whatever it is standing on. `shared_round` leaves the squares
 * blank (snakes and ladders still apply) and deals one exercise to every team
 * each round, cycling the selected pool.
 */
export const exerciseModeSchema = z.enum(['per_tile', 'shared_round']);
export type ExerciseMode = z.infer<typeof exerciseModeSchema>;

/** Missing or unrecognised values keep the original per-tile board. */
export function exerciseModeOf(value: unknown): ExerciseMode {
  return value === 'shared_round' ? 'shared_round' : 'per_tile';
}

export const gameKindSchema = z.enum(['snakes_and_ladders', 'find_four', 'describe_word']);
export type GameKind = z.infer<typeof gameKindSchema>;

export function gameKindOf(value: unknown): GameKind {
  if (value === 'find_four') return 'find_four';
  if (value === 'describe_word') return 'describe_word';
  return 'snakes_and_ladders';
}

/** Classroom name for Snákar og stigar, next to `FIND_FOUR_TITLE`. */
export const SNAKES_AND_LADDERS_TITLE = 'Snákar og stigar';

/** The name a teacher sees for a saved game's kind. */
export function gameKindLabel(kind: GameKind): string {
  if (kind === 'find_four') return FIND_FOUR_TITLE;
  if (kind === 'describe_word') return DESCRIBE_WORD_TITLE;
  return SNAKES_AND_LADDERS_TITLE;
}

/**
 * After the teacher starts the game, seats are permanent: a student keeps the
 * team they were on at kickoff, including across a logout and a same-name join.
 */
export function teamsLocked(status: SessionStatus): boolean {
  return status !== 'LOBBY';
}

/** Statuses in which a late joiner is still admitted (D-18). */
export const JOINABLE_STATUSES: readonly SessionStatus[] = [
  'LOBBY',
  'INTERMISSION',
  'IN_PROGRESS',
  'PAUSED',
];

export const teamViewSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  /** Palette key, resolved through `identityForKey` for colour and shape. */
  colorKey: z.string(),
  color: z.string(),
  onColor: z.string(),
  shape: teamShapeSchema,
  position: z.number().int().nonnegative(),
  /** Finndu fjögur upgrades this team has landed on. Omitted on snakes games. */
  powerups: z.array(z.enum(['shoes', 'hammer', 'bow', 'clothes'])).optional(),
});

export type TeamView = z.infer<typeof teamViewSchema>;

/**
 * One tile, as the teacher's board renders it.
 *
 * Lives here rather than with the round events because the session snapshot
 * carries the board, and a snapshot importing from `round.js` while `round.js`
 * imports the team view from here is a module cycle -- one that typechecks and
 * then fails at boot.
 */
export const boardTileViewSchema = z.object({
  position: z.number().int().nonnegative(),
  /** `PLAIN` and `EFFECT` are no longer generated; see `BoardTileType`. */
  type: z.enum(['START', 'PLAIN', 'MINIGAME', 'EFFECT', 'FINISH']),
  exerciseId: z.string().nullable(),
  exerciseLabel: z.string().nullable(),
  /** The snake or ladder starting here, for the board to draw (D-31). */
  link: z
    .object({
      to: z.number().int().nonnegative(),
      kind: z.enum(['snake', 'ladder']),
      label: z.string(),
    })
    .nullable(),
  label: z.string().nullable(),
  /** Lýstu orðinu category on this cell. Omitted on other boards. */
  topicId: describeCategorySchema.nullable().optional(),
});

export type BoardTileView = z.infer<typeof boardTileViewSchema>;

export const playerViewSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string(),
  teamId: z.string().uuid().nullable(),
  connected: z.boolean(),
  joinedAt: z.string(),
});

export type PlayerView = z.infer<typeof playerViewSchema>;

/** Everything the teacher's lobby renders. The board is a pure view of it (D-14). */
export const sessionSnapshotSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  status: sessionStatusSchema,
  gameName: z.string(),
  teams: z.array(teamViewSchema),
  players: z.array(playerViewSchema),
  /**
   * The board, carried in the snapshot rather than fetched separately.
   *
   * A reconnecting teacher must be able to draw the entire screen from one
   * message (D-14) -- a second round trip is one more thing to fail
   * mid-lesson. It comes from the session's frozen config once the game has
   * started, so it describes the board actually being played (§6.1).
   */
  board: z.array(boardTileViewSchema),
  /** Ordinal of the live or most recent round; null before the first. */
  roundOrdinal: z.number().int().positive().nullable(),
  /**
   * Absolute server timestamp the live round ends, epoch milliseconds.
   * Null between rounds, while paused, and before the first round.
   */
  roundClosesAt: z.number().int().positive().nullable(),
  /**
   * How this session assigns exercises. Omitted on snapshots from before the
   * shared-round option existed; treat missing as `per_tile`.
   */
  exerciseMode: exerciseModeSchema.optional(),
  /** The selected exercises, in the order the teacher picked them. */
  exercisePool: z.array(z.object({ id: z.string(), label: z.string() })).optional(),
  /**
   * The one exercise every team plays this round, or the one waiting next.
   * Null when the session is per-tile, or the pool is empty.
   */
  sharedRoundExercise: z
    .object({
      exerciseId: z.string(),
      label: z.string(),
    })
    .nullable()
    .optional(),
  /** Which board game this lobby is playing. Missing on older snapshots. */
  gameKind: gameKindSchema.optional(),
  /**
   * Finndu fjögur board + the live movement queue. Null/omitted on snakes.
   */
  findFour: z
    .object({
      redPositions: z.array(z.number().int().nonnegative()),
      powerups: z.array(
        z.object({
          position: z.number().int().nonnegative(),
          id: z.enum(['shoes', 'hammer', 'bow', 'clothes']),
        }),
      ),
      live: z
        .object({
          queue: z.array(
            z.object({
              teamId: z.string().uuid(),
              credit: z.number(),
              budget: z.number().int().nonnegative(),
            }),
          ),
          currentIndex: z.number().int().nonnegative(),
          awaitingBow: z.boolean(),
          killsAllowed: z.boolean(),
          bounceAfterBow: z.boolean().optional(),
        })
        .nullable(),
    })
    .optional(),
  /** Lýstu orðinu live turn. Null/omitted on other kinds. */
  describeWord: describeTeacherViewSchema.optional(),
  /** Lýstu orðinu teacher-phone pairing. Omitted on other kinds. */
  describeRemote: describeRemoteSchema.optional(),
});

export type SessionSnapshot = z.infer<typeof sessionSnapshotSchema>;

/**
 * What a player is told about the session.
 *
 * No roster, no team list, no board. Anyone can guess at access codes, so a
 * player connection must not be able to describe a classroom.
 */
export const playerSessionViewSchema = z.object({
  id: z.string().uuid(),
  status: sessionStatusSchema,
  gameName: z.string(),
});

export type PlayerSessionView = z.infer<typeof playerSessionViewSchema>;

/**
 * The exercise waiting on the tile the player's team is standing on.
 *
 * Sent to the player because otherwise they cannot know it. The board is on a
 * projector across the room and a tile carries a three-letter code; the team
 * that has just landed on `MM` finds out it means `Miðmynd` when the first
 * question of the next round appears, which is exactly too late to think about
 * it. Naming it on their own screen turns the wait between rounds into a
 * moment to prepare, and it is the same code and label they see on the tile and
 * in `/leika` (§1.7).
 *
 * Just the one tile, not the board: a player connection must still not be able
 * to describe a classroom.
 */
export const nextExerciseViewSchema = z.object({
  exerciseId: z.string(),
  label: z.string(),
  /** Which square the team is standing on, so the tile can be found on it. */
  tilePosition: z.number().int().nonnegative(),
});

export type NextExerciseView = z.infer<typeof nextExerciseViewSchema>;

export const playerStateSchema = z.object({
  player: playerViewSchema,
  /** Null while the player is waiting to be placed by the teacher (D-18). */
  team: teamViewSchema.nullable(),
  session: playerSessionViewSchema,
  /** Null with no team, or before the board has been frozen into the session. */
  nextExercise: nextExerciseViewSchema.nullable(),
  /** Live round wall clock; null when no round is running. */
  roundClosesAt: z.number().int().positive().nullable(),
  /** Lýstu orðinu speaker/guesser view. Omitted on other kinds. */
  describe: describePlayerViewSchema.optional(),
});

export type PlayerState = z.infer<typeof playerStateSchema>;

/* ---------------------------------------------------------------
 * Inbound payloads. Every one of these is parsed at the socket
 * boundary before a handler sees it (§9.4).
 * ------------------------------------------------------------- */

/** Bounds match the DB column and the lobby UI; see `guardDisplayName`. */
export const DISPLAY_NAME_MIN = 2;
export const DISPLAY_NAME_MAX = 24;

export const displayNameSchema = z.string().trim().min(DISPLAY_NAME_MIN).max(DISPLAY_NAME_MAX);

export const playerJoinSchema = z.object({
  code: z.string().trim().min(1).max(20),
  displayName: displayNameSchema,
});

export type PlayerJoinInput = z.infer<typeof playerJoinSchema>;

export const playerReconnectSchema = z.object({
  token: z.string().min(1).max(200),
});

export const joinResultSchema = playerStateSchema.extend({
  /**
   * Opaque, shown once. The client stores it in localStorage and presents it
   * to reclaim this identity after a refresh (D-14). Only the digest is
   * persisted server-side.
   */
  reconnectToken: z.string(),
});

export type JoinResult = z.infer<typeof joinResultSchema>;

export const teamAssignSchema = z.object({
  playerId: z.string().uuid(),
  /** Null returns the player to the unassigned holding area (D-18). */
  teamId: z.string().uuid().nullable(),
});

export const teamNameSchema = z.string().trim().min(1).max(40);

export const teamCreateSchema = z.object({
  name: teamNameSchema.optional(),
});

export const teamRenameSchema = z.object({
  teamId: z.string().uuid(),
  name: teamNameSchema,
});

export const teamRemoveSchema = z.object({
  teamId: z.string().uuid(),
});

export const playerRenameSchema = z.object({
  playerId: z.string().uuid(),
  displayName: displayNameSchema,
});

export const playerRemoveSchema = z.object({
  playerId: z.string().uuid(),
});
